import React from 'react';
import { AppBar, Toolbar, Typography, Box, Button, Menu, MenuItem, Divider, IconButton, Chip, Tooltip } from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Menu as MenuIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  BugReport as BugIcon,
  Lightbulb as IdeaIcon,
  SmartToy as RobotIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useEnvironment, useFeature } from '../EnvironmentContext';
import { palette } from '../ui-theme';
import { useColorMode } from '../ColorModeContext';
import FeedbackSubmitModal, { type FeedbackKind } from './FeedbackSubmitModal';
import ViewAsDialog from './ViewAsDialog';
import { setViewAs } from '../api/viewAs';
import { useAssistant } from '../AssistantContext';
import AssistantAskBar from './AssistantAskBar';

interface HeaderProps {
  title: string;
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}

const envColors: Record<string, string> = {
  Dev: 'warning',
  Local: 'warning',
  UAT: 'error',
};

const Header: React.FC<HeaderProps> = ({ title, onToggleSidebar, sidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { envName, configurationProfile } = useEnvironment();
  const restricted = configurationProfile === 'IAMPProdOnly';
  const { mode, toggleColorMode } = useColorMode();
  const { user, hasRole, logout } = useAuth();
  const [feedbackKind, setFeedbackKind] = React.useState<FeedbackKind | null>(null);
  const [viewAsOpen, setViewAsOpen] = React.useState(false);
  const assistantFeature = useFeature('Assistant.Guidance');
  const assistantEnabled = !restricted && assistantFeature;
  // Home renders its own centered composer (see HomePage), so the header's bar would be a second
  // ask box stacked on the same screen - the exact clutter that composer was built to avoid.
  const showAskBar = assistantEnabled && location.pathname !== '/';
  const { openAssistant } = useAssistant();

  const viewAs = user?.viewAs;
  // Offered on the strength of the caller's *unsuppressed* roles, so an admin who has hidden their
  // admin role can still find the way back out.
  const canViewAs = !!viewAs && viewAs.availableRoles.includes('AskIAM.Admin');

  const exitViewAs = () => {
    void setViewAs([]).then(() => window.location.reload());
  };

  const displayName = user?.name || user?.email || 'User';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    handleMenuClose();
    logout();
  };

  const showBadge = envName && envName !== 'Prod';
  const badgeColor = envName ? (envColors[envName] ?? 'warning') as 'warning' | 'error' : undefined;

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: palette.header.background,
        // AppBar has no explicit `color` prop, so its MuiAppBar-colorPrimary
        // class would otherwise set text/icon colour (everything here is
        // color="inherit") from theme.palette.primary.contrastText, which is
        // mode-aware (#121212 in dark mode) while this background is a flat
        // literal in both modes. Forcing it keeps the 4.53 contrast ratio
        // documented in palette.header's comment true in dark mode too.
        color: palette.header.text,
        // The theme rounds every Paper, and AppBar is a Paper, so the bar that
        // spans the whole viewport was getting corners it was never meant to
        // have. Unrelated to the contrast fix below - that hypothesis was
        // tested and disproved by a scan - but wrong on its own terms.
        borderRadius: 0,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ px: 3 }}>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onToggleSidebar}
          sx={{ mr: 2 }}
          aria-label="Toggle sidebar navigation"
          aria-expanded={sidebarOpen}
          aria-controls={sidebarOpen ? 'sidebar-navigation' : undefined}
        >
          <MenuIcon />
        </IconButton>
        <Box
          component="img"
          src="/askiam-icon.png"
          alt=""
          sx={{ width: 32, height: 32, mr: 1.5, display: 'block', alignSelf: 'center' }}
        />
        <Box component="h1" sx={{ m: 0, display: 'flex', fontSize: 'inherit', flexShrink: 0 }}>
        <Button
          variant="text"
          onClick={() => navigate('/')}
          sx={{
            justifyContent: 'flex-start',
            // Was 'primary.contrastText', which is mode-aware (#121212 in dark
            // mode) and silently went black against this header's flat colour -
            // the same class of bug fixed on the AppBar itself below. 'inherit'
            // picks up the AppBar's explicit color instead, like every other
            // element in this header already does via color="inherit".
            color: 'inherit',
            textTransform: 'none',
            fontSize: '1.25rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          {title}
        </Button>
        </Box>
        {/* Always present (not conditionally rendered) so badges/icons stay pinned to the right
            whether or not the ask bar itself is showing - home route, flag off, or a narrow
            viewport (hidden below md; the robot icon in the action cluster still opens it). The
            px itself must match that same md breakpoint, or a mobile header reserves padding for
            a bar that isn't there. */}
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', px: { xs: 0, md: showAskBar ? 2 : 0 } }}>
          {showAskBar && (
            <AssistantAskBar sx={{ width: '100%', maxWidth: 560, display: { xs: 'none', md: 'block' } }} />
          )}
        </Box>
        {showBadge && (
          <Chip
            label={envName!.toUpperCase()}
            size="small"
            color={badgeColor}
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: 1,
              height: 24,
              mr: 1,
            }}
          />
        )}
        {viewAs?.active && (
          <Tooltip title={`Hiding ${viewAs.suppressedRoles.length} of your roles — click to restore full access`}>
            <Chip
              label="VIEWING AS USER"
              size="small"
              color="secondary"
              onClick={exitViewAs}
              onDelete={exitViewAs}
              sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: 1, height: 24, mr: 1 }}
            />
          </Tooltip>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {assistantEnabled && (
            <Tooltip title="Ask the AskIAM Assistant">
              <IconButton color="inherit" onClick={openAssistant} aria-label="Ask the AskIAM Assistant">
                <RobotIcon />
              </IconButton>
            </Tooltip>
          )}
          {!restricted && <Tooltip title="Report a bug">
            <IconButton color="inherit" onClick={() => setFeedbackKind('bug')} aria-label="Report a bug">
              <BugIcon />
            </IconButton>
          </Tooltip>}
          {!restricted && <Tooltip title="Share an idea">
            <IconButton color="inherit" onClick={() => setFeedbackKind('idea')} aria-label="Share an idea">
              <IdeaIcon />
            </IconButton>
          </Tooltip>}
          <IconButton
            color="inherit"
            onClick={toggleColorMode}
            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <Button
            color="inherit"
            onClick={handleMenuOpen}
            endIcon={<ArrowDropDownIcon />}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            aria-controls={anchorEl ? 'user-menu' : undefined}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              // This is the only text in the header drawn with `color="inherit"`,
              // and a contrast checker resolved its background all the way past
              // the Toolbar and the AppBar to <body> (#f5f5f5), reporting white
              // on near-white at 1.09. Naming the colour it is already sitting on
              // is a visual no-op and removes the dependency on how far up the
              // tree a checker has to walk to find an answer. Its siblings are
              // icon-only, which is why nothing else here was reported.
              backgroundColor: palette.header.background,
            }}
          >
            {displayName}
          </Button>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { navigate('/my-requests'); handleMenuClose(); }}>
              My Requests
            </MenuItem>
            <MenuItem onClick={() => { navigate('/claims'); handleMenuClose(); }}>
              My Claims
            </MenuItem>
            <MenuItem onClick={() => { navigate('/whoami'); handleMenuClose(); }}>
              Who Am I
            </MenuItem>
            {canViewAs && <Divider />}
            {canViewAs && (
              <MenuItem onClick={() => { setViewAsOpen(true); handleMenuClose(); }}>
                {viewAs?.active ? 'Change view as…' : 'View as…'}
              </MenuItem>
            )}
            {canViewAs && viewAs?.active && (
              <MenuItem onClick={() => { handleMenuClose(); exitViewAs(); }}>
                Restore full access
              </MenuItem>
            )}
            <Divider />
            {user?.multiTenantEnabled && (
              <MenuItem onClick={() => {
                handleMenuClose();
                window.location.href = '/api/auth/login?switchAccount=true';
              }}>Switch account or tenant</MenuItem>
            )}
            <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      {!restricted && feedbackKind && (
        <FeedbackSubmitModal
          open
          presetKind={feedbackKind}
          onClose={() => setFeedbackKind(null)}
        />
      )}
      {viewAs && (
        <ViewAsDialog open={viewAsOpen} onClose={() => setViewAsOpen(false)} state={viewAs} />
      )}
    </AppBar>
  );
};

export default Header;
